/**
 * F17 — stałe współdzielone między apps/api (engine, validators, publish-validation)
 * i apps/admin (ConditionEditor, walidacje frontendowe, dropdown wyboru drogi + projekcja
 * krawędzi). Np. PROCESS_EDGE_TYPES zasila zapis grafu, listę dróg w silniku, walidację
 * publikacji ORAZ kreator — z jednej definicji.
 *
 * Single source of truth — zmiana wartości tutaj propaguje się do obu warstw.
 * Plan: docs/phases/PHASE_17_PLAN.md §T17.1.5.
 */

/**
 * Maksymalna głębokość zagnieżdżenia w ConditionEditor (drzewo AND/OR z warunków).
 *
 * Root grupa = poziom 0; każda zagnieżdżona grupa zwiększa poziom o 1.
 * Wartość = 5 oznacza root + 5 poziomów zagnieżdżenia → 99% realnych przypadków.
 *
 * Walidacja:
 * - Frontend: `ConditionEditor` blokuje przycisk "+ Grupa" po osiągnięciu limitu.
 * - Backend: `apps/api/lib/processes/conditions.ts` validator zwraca 400
 *   `CONDITION_TOO_DEEP` przy zapisie głębszego drzewa.
 */
export const MAX_CONDITION_DEPTH = 5;

/** 5 typów węzłów procesu — spójne z `apps/api/lib/validators/processes.ts`. */
export const PROCESS_NODE_TYPES = [
  'start',
  'decision',
  'parallel_split',
  'parallel_join',
  'end',
] as const;

export type ProcessNodeType = (typeof PROCESS_NODE_TYPES)[number];

/**
 * Typy krawędzi (dróg) procesu — model WF: decyzja = wybór drogi narysowanej w definicji.
 * `unconditional` — droga zawsze dostępna; `conditional` — droga widoczna tylko, gdy jej
 * warunek jest spełniony (użytkownik i tak wybiera sam). Tę samą listę pilnuje baza
 * (ograniczenie na `process_edges.edge_type`, migracja PUB 0180). Single source of truth
 * dla zapisu grafu, silnika, publikacji, listy decyzji i kreatora.
 *
 * Dodanie typu drogi to nie tylko wpis tutaj: wymaga nowej migracji PUB, która podmienia
 * ograniczenie `chk_process_edges_edge_type` (z kolokowanym testem integracyjnym — inaczej
 * zapis krawędzi nowego typu odbije się od bazy), oraz decyzji, czy autor procesu może ten typ
 * rysować w kreatorze (`AUTHORING_EDGE_TYPES` — dziś to ta sama lista).
 */
export const PROCESS_EDGE_TYPES = ['unconditional', 'conditional'] as const;

export type ProcessEdgeType = (typeof PROCESS_EDGE_TYPES)[number];

/**
 * Typy krawędzi, które autor procesu może narysować w kreatorze — to te same typy co
 * `PROCESS_EDGE_TYPES` (jedna lista, nazwa zostaje dla kreatora i walidatora zapisu grafu).
 */
export const AUTHORING_EDGE_TYPES = PROCESS_EDGE_TYPES;

export type AuthoringEdgeType = ProcessEdgeType;

/**
 * Czy krawędź danego typu może być drogą do wyboru w decyzji — każdy znany typ krawędzi
 * (`PROCESS_EDGE_TYPES`). `conditional` jest drogą tylko przy spełnionym warunku (sprawdza
 * to wołający przez `isConditionMet`). Nieznana wartość (np. uszkodzona kopia definicji
 * w sprawie) drogą nie jest.
 */
export function isManualRouteEdgeType(edgeType: string): boolean {
  return (PROCESS_EDGE_TYPES as readonly string[]).includes(edgeType);
}

/**
 * Pola wymagane drogi — klucz `'default'` dla celów bez klasy (kontrahenci, moduły
 * własne) albo klasa dokumentu (`crm_core.documents`); wartość = slugi pól, które
 * muszą być niepuste, żeby wybrać tę drogę. Kształt 1:1 z dawną regułą per etap.
 */
export type RequiredFieldsRule = Partial<Record<string, string[]>>;

/** Minimalny kształt węzła potrzebny do wyliczenia dróg (snapshot sprawy albo graf kreatora). */
export interface RouteGraphNode {
  id: string;
  nodeType: string;
  label: string | null;
}

/** Minimalny kształt krawędzi potrzebny do wyliczenia dróg (snapshot sprawy albo graf kreatora). */
export interface RouteGraphEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeType: string;
  label: string | null;
  /** Kolejność dróg w obrębie etapu źródłowego, ustawiana przez admina. */
  sortOrder: number;
  /**
   * Droga domyślna etapu: jedna zwykła albo kilka warunkowych, bez mieszania (pilnuje publikacja);
   * przy kilku widocznych obowiązuje pierwsza wg kolejności.
   */
  isDefault: boolean;
  /** Wybór tej drogi wymaga komentarza. */
  requireComment: boolean;
}

/**
 * Droga dostępna dla osoby decydującej na etapie. `routeKey` = id krawędzi ze snapshotu
 * sprawy — wysyłany z powrotem w decyzji. Single source of truth dla serwera (silnik,
 * lista decyzji, detal sprawy) i panelu (przyciski dróg, okno „Akceptacja").
 */
export interface AvailableRoute {
  routeKey: string;
  /** Nazwa drogi; droga bez nazwy dostaje nazwę etapu docelowego; `''` gdy i ten jej nie ma. */
  label: string;
  isDefault: boolean;
  targetNodeId: string;
  targetLabel: string | null;
  /** Droga prowadzi do etapu końcowego — okno nie pokazuje wtedy pola akceptanta. */
  targetIsEnd: boolean;
  requireComment: boolean;
}

/**
 * Drogi dostępne na etapie `nodeId`, w kolejności wyświetlania: najpierw droga domyślna,
 * potem kolejność ustawiona przez admina (`sortOrder`), remis — po id. Droga warunkowa
 * wchodzi tylko, gdy `isConditionMet(edge)` zwraca `true`; warunek liczy wołający
 * (serwer — ze snapshotu danych sprawy), bo ewaluator warunków żyje po stronie API.
 */
export function listAvailableRoutes<E extends RouteGraphEdge>(params: {
  nodes: readonly RouteGraphNode[];
  edges: readonly E[];
  nodeId: string;
  isConditionMet: (edge: E) => boolean;
}): AvailableRoute[] {
  const nodeById = new Map(params.nodes.map((n) => [n.id, n]));
  const candidates = params.edges
    .filter((e) => e.sourceNodeId === params.nodeId && isManualRouteEdgeType(e.edgeType))
    .filter((e) => e.edgeType !== 'conditional' || params.isConditionMet(e))
    .sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.id.localeCompare(b.id);
    });
  return candidates.map((e) => {
    const target = nodeById.get(e.targetNodeId);
    const targetLabel = target?.label?.trim() || null;
    return {
      routeKey: e.id,
      label: e.label?.trim() || targetLabel || '',
      isDefault: e.isDefault,
      targetNodeId: e.targetNodeId,
      targetLabel,
      targetIsEnd: target?.nodeType === 'end',
      requireComment: e.requireComment,
    };
  });
}

/** Czy osoba decydująca musi wskazać drogę — gdy dróg do wyboru jest co najmniej dwie. */
export function requiresRouteChoice(routes: readonly AvailableRoute[]): boolean {
  return routes.length >= 2;
}

/**
 * Droga podpowiadana w oknie decyzji i progowa w głosowaniu: droga domyślna, a gdy etap ma
 * tylko jedną drogę — ta jedna. `null` = bez wskazania przez człowieka nie da się wybrać.
 * Domyślnych dróg może być kilka (warunkowe, tylko te o spełnionym warunku trafiają do `routes`) —
 * wygrywa pierwsza wg kolejności `listAvailableRoutes`.
 *
 * „Akceptuj zaznaczone" tej funkcji NIE używa: jedna widoczna droga z kilku nie może przejść hurtem
 * tylko dlatego, że warunki ukryły resztę — hurt ma własną regułę (`bulkFallbackRoute` w silniku,
 * ADR-0111 pkt 4).
 */
export function findDefaultRoute(routes: readonly AvailableRoute[]): AvailableRoute | null {
  const marked = routes.find((r) => r.isDefault);
  if (marked) return marked;
  const [only] = routes;
  return routes.length === 1 && only ? only : null;
}

/**
 * Wersja kształtu zapisu grafu (`PATCH /api/processes/:id/graph`). Serwer odrzuca zapis
 * z inną wersją — stara, nieodświeżona karta kreatora nie skasuje po cichu pól dróg
 * i etapów (zapis grafu to pełna podmiana), tylko dostanie prośbę o odświeżenie.
 *
 * Historia: 2 — ustawienia dróg (model WF); 3 — flaga etapu „Pozwól cofnąć do poprzedniego
 * etapu" (`allowStepBack`).
 */
export const PROCESS_GRAPH_SCHEMA_VERSION = 3;

/**
 * Etap bez akceptantów z odpowiedzi 422 `NO_APPROVERS` (start sprawy albo decyzja) — jeden
 * kontrakt API ↔ panel (`details.stages` silnika, `stages` w odpowiedzi tras).
 */
export interface NoApproversStage {
  nodeId: string;
  /** Nazwa etapu z definicji sprawy; `null`, gdy etap jej nie ma. */
  label: string | null;
}

/**
 * Prawa obiegu zalogowanego użytkownika w firmie REKORDU (nie w firmie aktywnej w aplikacji —
 * decyzja właściciela 2026-10-08, „firma z rekordu, nie ze sprawy"), liczone przez serwer:
 * `canStart` = „Wyślij do akceptacji" — ta sama bramka co sam start (rekord istnieje i nie jest
 * usunięty, dostęp do firmy rekordu, `processes.instances.create` w niej, prawo podglądu rekordu), więc
 * także właściciel tenanta może dostać `false`; `canCancel` = „Anuluj proces"
 * (`processes.instances.cancel` w firmie rekordu). Jeden kontrakt API ↔ panel
 * (`recordPermissions` w odpowiedzi listingu spraw rekordu). Flagi służą widoczności przycisków —
 * prawo i tak sprawdza serwer przy akcji.
 *
 * Pytający BEZ prawa podglądu spraw rekordu (bez `processes.instances.view` w firmie rekordu ALBO
 * bez prawa podglądu samego rekordu — decyzja właściciela 2026-10-12, „karta rekordu dla
 * decydującego") dostaje
 * listę zawężoną do spraw, w których może teraz decydować, więc flagi znaczą wtedy:
 *   - `canStart` — ta sama bramka startu ORAZ rekord bez żadnej sprawy innej niż anulowana: panel
 *     nie widzi sprawy, która zajmuje rekord, więc o zajętości mówi sama flaga („Wyślij do
 *     akceptacji" znika bez ujawniania sprawy),
 *   - `canCancel` — zawsze `false` (anulowanie wymaga podglądu spraw).
 * Z prawem podglądu `canStart` liczy wyłącznie bramkę startu — o zajętym rekordzie decyduje panel
 * z pełnej listy spraw.
 */
export interface RecordProcessPermissions {
  canStart: boolean;
  canCancel: boolean;
}

/** Kody wpisów eskalacji po terminie — wszystkie niosą przełożonego (`params.supervisorUserId`). */
export const PROCESS_ESCALATION_SYSTEM_REASON_CODES = [
  'ESCALATED_TO_SUPERVISOR',
  'SUPERVISOR_NOTIFIED',
] as const;

export type ProcessEscalationSystemReasonCode =
  (typeof PROCESS_ESCALATION_SYSTEM_REASON_CODES)[number];

/**
 * Powody wpisów automatycznych w historii sprawy (decyzje bez osoby decydującej). Silnik zapisuje
 * kod z parametrami (`process_decisions.system_reason`), a panel tłumaczy go na tekst w języku
 * użytkownika — zamiast gotowego zdania w komentarzu.
 *   - `TARGET_NOT_FOUND` / `TARGET_SOFT_DELETED` — rekord sprawy zniknął / został usunięty
 *     w trakcie obiegu (sprawa anulowana automatycznie),
 *   - `OTHER_BRANCH_ENDED` — inna gałąź obiegu równoległego zakończyła sprawę (krok pominięty;
 *     `endLabel` — nazwa etapu końcowego, gdy jest),
 *   - `OTHER_BRANCH_ERRORED` — inna gałąź nie mogła pójść dalej i sprawa przeszła w błąd,
 *   - `JOIN_WITHOUT_OPEN_BRANCHES` — scalenie czekało na gałąź, w której nie było już żadnego
 *     otwartego etapu (błąd definicji procesu, której publikacja nie przepuszcza); sprawa przeszła
 *     w błąd, a pomijany jest sam krok scalenia,
 *   - `ESCALATED_TO_SUPERVISOR` — termin sprawy minął, decyzja na etapie przeszła na przełożonego
 *     akceptanta (wpis eskalacji, typ `automated_escalation`),
 *   - `SUPERVISOR_NOTIFIED` — termin sprawy minął, przełożonego akceptanta tylko powiadomiono
 *     mailem: nie ma prawa podejmowania decyzji w firmie rekordu, więc decyzja na niego nie przeszła
 *     (wpis eskalacji, typ `automated_escalation`).
 */
export const PROCESS_DECISION_SYSTEM_REASON_CODES = [
  'TARGET_NOT_FOUND',
  'TARGET_SOFT_DELETED',
  'OTHER_BRANCH_ENDED',
  'OTHER_BRANCH_ERRORED',
  'JOIN_WITHOUT_OPEN_BRANCHES',
  ...PROCESS_ESCALATION_SYSTEM_REASON_CODES,
] as const;

export type ProcessDecisionSystemReasonCode = (typeof PROCESS_DECISION_SYSTEM_REASON_CODES)[number];

/** Czy kod powodu jest kodem eskalacji po terminie (jedno źródło: `PROCESS_ESCALATION_SYSTEM_REASON_CODES`). */
export function isProcessEscalationSystemReasonCode(
  code: string,
): code is ProcessEscalationSystemReasonCode {
  return (PROCESS_ESCALATION_SYSTEM_REASON_CODES as readonly string[]).includes(code);
}

/**
 * Powód wpisu automatycznego — unia rozłączna po `code`: `OTHER_BRANCH_ENDED` ma `endLabel` (nazwa
 * etapu końcowego, pomijana, gdy etap jej nie ma), kody eskalacji — `supervisorUserId` (przełożony,
 * na którego przeszła decyzja albo którego powiadomiono; nazwę liczy panel), pozostałe kody mają
 * pusty obiekt `params`.
 */
export type ProcessDecisionSystemReason =
  | { code: 'OTHER_BRANCH_ENDED'; params: { endLabel?: string } }
  | { code: ProcessEscalationSystemReasonCode; params: { supervisorUserId: string } }
  | {
      code: Exclude<
        ProcessDecisionSystemReasonCode,
        'OTHER_BRANCH_ENDED' | ProcessEscalationSystemReasonCode
      >;
      params: Record<string, never>;
    };

/** 10 operatorów warunku — symbole matematyczne (nie aliasy `eq/gt`). */
export const CONDITION_OPERATORS = [
  '==',
  '!=',
  '>',
  '<',
  '>=',
  '<=',
  'in',
  'contains',
  'is_null',
  'is_not_null',
] as const;

export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];

/** 5 typów akceptantów. */
export const APPROVER_TYPES = [
  'specific_users',
  'roles',
  'record_owner',
  'submitter_supervisor',
  'department_manager',
] as const;

export type ApproverType = (typeof APPROVER_TYPES)[number];

/** 3 tryby decyzji multi-approver. */
export const MULTI_APPROVER_MODES = ['any', 'all', 'majority'] as const;

export type MultiApproverMode = (typeof MULTI_APPROVER_MODES)[number];

/**
 * Typy akceptantów z pulą kandydatów wskazaną w definicji (konkretne osoby, role) — jedyne, dla
 * których ma sens wskazanie adresata (`requireAddresseeDesignation` / `defaultAddresseeUserId`).
 * Pozostałe typy wyznacza sam rekord albo struktura organizacyjna: przełożony i kierownik to jedna
 * osoba, opiekun rekordu też — poza kontrahentem, gdzie to do dwóch osób (właściciel i osoba
 * przypisana, `resolveByRecordOwner` w `apps/api/lib/processes/resolver.ts`). Adresata się w nich
 * nie wskazuje — serwer odrzuca taką konfigurację.
 */
export const MULTI_PERSON_APPROVER_TYPES: ReadonlySet<ApproverType> = new Set<ApproverType>([
  'specific_users',
  'roles',
]);

/**
 * Pola konfiguracji akceptantów etapu, z których wynika pole „akceptant na następnym etapie”.
 * Kształt wspólny dla konfiguracji silnika (`ApproverConfig` w `apps/api/lib/processes/types.ts`)
 * i panelu (konfiguracja etapu z kopii definicji sprawy — tam `type` bywa nieustawiony).
 */
export interface AddresseeFieldRuleInput {
  type?: ApproverType;
  mode?: MultiApproverMode;
  requireAddresseeDesignation?: boolean;
  defaultAddresseeUserId?: string;
}

/** Pole „akceptant na następnym etapie” dla etapu docelowego — wynik `addresseeFieldRule`. */
export interface AddresseeFieldRule {
  /**
   * `true` — etap ma „wymagaj wskazania”: bez wybranej osoby decyzji nie da się wykonać.
   * `false` — wskazanie opcjonalne (etap w trybie „dowolny”): puste pole = cała pula etapu.
   */
  required: boolean;
  /** Domyślny adresat etapu (`defaultAddresseeUserId`; pusty tekst = brak); `null` = bez podpowiedzi. */
  defaultUserId: string | null;
}

/**
 * Czy etap decyzji dostaje pole „akceptant na następnym etapie” (jak w starym WF), czy jest ono
 * wymagane i kto jest kandydatem domyślnym. Jedna reguła dla panelu (okno „Akceptacja” — które
 * pola pokazać i kogo wstępnie wybrać, `apps/admin/src/lib/processes/addressee-resolution.ts`)
 * i silnika (decyzja hurtowa podstawia domyślnego adresata tylko tam, gdzie okno pokazałoby pole —
 * `defaultAddresseeCandidate` w `apps/api/lib/processes/engine.ts`).
 *
 * Reguły (konfiguracja jednego etapu docelowego; `null` = etap bez pola):
 *   - typ akceptantów spoza `MULTI_PERSON_APPROVER_TYPES` (opiekun rekordu, przełożony
 *     zgłaszającego, kierownik działu) albo brak typu / konfiguracji → `null`,
 *   - etap ma „wymagaj wskazania” (`requireAddresseeDesignation === true`) → pole wymagane
 *     (każdy tryb),
 *   - etap w trybie „dowolny” (`any`, także brak trybu — silnik przyjmuje wtedy `any`) bez flagi →
 *     pole opcjonalne (puste = cała pula),
 *   - etap w trybie „wszyscy” / „większość” bez flagi → `null` (wskazanie jednej osoby zmieniłoby
 *     regułę głosowania etapu; silnik odrzuca je jako `ADDRESSEE_NOT_ALLOWED`).
 * Czy domyślny adresat należy do puli etapu, rozstrzyga wołający (panel — po pobraniu puli, silnik
 * — z puli rozwiązanej przez resolver). Czy cel drogi to etap decyzji i czy głos zamyka bieżący
 * etap — też wołający.
 */
export function addresseeFieldRule(
  config: AddresseeFieldRuleInput | null | undefined,
): AddresseeFieldRule | null {
  if (!config?.type || !MULTI_PERSON_APPROVER_TYPES.has(config.type)) return null;

  const required = config.requireAddresseeDesignation === true;
  if (!required && (config.mode ?? 'any') !== 'any') return null;

  return { required, defaultUserId: config.defaultAddresseeUserId || null };
}

/**
 * Czy tryb akceptantów to „kworum" (`all` — wszyscy, `majority` — większość): domyślna jest tam
 * jedna i zwykła, więc domyślna droga warunkowa jest zakazana (reguła publikacji
 * `DECISION_NODES_CONDITIONAL_DEFAULT_ROUTE`), a etap z ≥ 2 drogami musi mieć drogę domyślną.
 * W trybie `any` (i bez trybu) dozwolona domyślna warunkowa. Podawaj tryb z konfiguracji etapu
 * (`approverConfig.mode ?? 'any'`) — tak, jak liczy go silnik.
 */
export function isQuorumApprovalMode(mode: MultiApproverMode | undefined): boolean {
  return mode === 'all' || mode === 'majority';
}

/**
 * Najdłuższy komentarz do decyzji (znaki po obcięciu spacji). Jedno źródło dla walidatora serwera
 * (decyzja pojedyncza i masowa oraz komentarz zgłaszającego przy wysyłce — `initiatorComment`
 * startu sprawy) i pól komentarza w panelu (okno „Akceptacja" i okno wysyłki).
 */
export const PROCESS_DECISION_COMMENT_MAX_LENGTH = 2000;

/**
 * Najwięcej pozycji w jednym żądaniu decyzji masowej. Jedno źródło dla walidatora serwera i panelu,
 * który dzieli „Akceptuj zaznaczone" na paczki tej wielkości.
 */
export const PROCESS_BULK_DECISIONS_MAX_ITEMS = 100;

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString);
}

/** Pole opcjonalne: brak (`undefined`) albo wartość spełniająca strażnika — `null` nie przechodzi. */
function isOptional<T>(value: unknown, guard: (candidate: unknown) => candidate is T): boolean {
  return value === undefined || guard(value);
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

/**
 * Czy konfiguracja akceptantów etapu jest kompletna — ta sama reguła, którą przy publikacji
 * procesu stosuje serwer (`ApproverConfigSchema` w `apps/api/lib/validators/processes.ts`;
 * trasa publikacji traktuje etap z niepoprawną konfiguracją jak etap bez akceptantów).
 * Panel (lista kontrolna publikacji i klocek etapu na schemacie) liczy to tą samą funkcją, żeby
 * ostrzeżenie nie rozjeżdżało się z odmową serwera.
 *
 * Sprawdza:
 *  - konfiguracja jest obiektem (nie `null`, nie tablicą, nie wartością prostą),
 *  - `type` należy do `APPROVER_TYPES`,
 *  - `mode` (opcjonalny) należy do `MULTI_APPROVER_MODES`,
 *  - `specific_users` → `userIds` to niepusta tablica; `roles` → `roleTemplateIds` to niepusta
 *    tablica (przy pozostałych typach te pola nie są wymagane),
 *  - wskazanie adresata (`requireAddresseeDesignation === true` albo ustawiony
 *    `defaultAddresseeUserId`) jest dozwolone wyłącznie dla typów wieloosobowych
 *    (`MULTI_PERSON_APPROVER_TYPES`),
 *  - kształt pól opcjonalnych jak w schemacie: `userIds` / `roleTemplateIds` to tablice tekstów,
 *    `requireAddresseeDesignation` to wartość logiczna, `defaultAddresseeUserId` to tekst.
 *
 * NIE sprawdza formatu UUID (`userIds`, `roleTemplateIds`, `defaultAddresseeUserId`) — panel
 * wybiera identyfikatory wyłącznie z list wyboru, więc ten format weryfikuje tylko serwer. Tę
 * świadomą rozbieżność przypina osobny test „znana rozbieżność” w tym samym pliku testów co niżej.
 *
 * To lustro `ApproverConfigSchema`: zgodność obu implementacji pilnuje test w
 * `apps/api/lib/validators/processes.test.ts` — zmiana reguły po jednej stronie musi iść w parze
 * ze zmianą po drugiej.
 */
export function isApproverConfigComplete(config: unknown): boolean {
  if (typeof config !== 'object' || config === null || Array.isArray(config)) return false;
  const fields = config as Record<string, unknown>;

  const approverType = APPROVER_TYPES.find((candidate) => candidate === fields.type);
  if (approverType === undefined) return false;

  const { mode, userIds, roleTemplateIds, requireAddresseeDesignation, defaultAddresseeUserId } =
    fields;
  if (mode !== undefined && !MULTI_APPROVER_MODES.some((candidate) => candidate === mode)) {
    return false;
  }
  if (!isOptional(userIds, isStringArray)) return false;
  if (!isOptional(roleTemplateIds, isStringArray)) return false;
  if (!isOptional(requireAddresseeDesignation, isBoolean)) return false;
  if (!isOptional(defaultAddresseeUserId, isString)) return false;

  if (approverType === 'specific_users' && !(isStringArray(userIds) && userIds.length > 0)) {
    return false;
  }
  if (approverType === 'roles' && !(isStringArray(roleTemplateIds) && roleTemplateIds.length > 0)) {
    return false;
  }

  const designatesAddressee =
    requireAddresseeDesignation === true || defaultAddresseeUserId !== undefined;
  return !designatesAddressee || MULTI_PERSON_APPROVER_TYPES.has(approverType);
}

/**
 * Priorytet/pilność wniosku procesu — kolumna `process_instances.priority` (migracja 0124).
 *
 * Single source of truth dla:
 *   - backend walidatora (`apps/api/lib/validators/processes.ts` — Zod enum na starcie/edycji),
 *   - frontend (picker pilności przy starcie procesu + badge na kokpicie decyzji),
 *   - i18n (klucze etykiet per wartość).
 *
 * Kolejność = malejąca pilność (urgent → low). Domyślnie `DEFAULT_DECISION_PRIORITY` ('normal').
 * Wartości pilnuje warstwa aplikacji (Zod); kolumna to `varchar(10)` bez CHECK — lustro `status`.
 */
export const DECISION_PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;

export type DecisionPriority = (typeof DECISION_PRIORITIES)[number];

export const DEFAULT_DECISION_PRIORITY: DecisionPriority = 'normal';

/**
 * Wymiary grupowania kokpitu decyzji (grupowanie server-side na agregacji SQL). UNIA wszystkich
 * wymiarów; biała lista PER EKRAN niżej (`*_GROUP_BY`). Single source of truth — importowane przez:
 * walidator (`apps/api/lib/validators/processes.ts`, biała lista `?groupBy=`), silnik grupowania
 * (`apps/api/lib/processes/decisions-list/grouping.ts`) oraz front (selektor wymiaru + tłumaczenia
 * etykiet grup na kokpicie „Akceptacje").
 *
 *   - `process`          — po definicji procesu
 *   - `target_class`     — po klasie dokumentu (cele `crm_core.documents`) lub kodzie modułu (kartoteka)
 *   - `submitter`        — po składającym wniosek
 *   - `priority`         — po pilności (urgent…low)
 *   - `deadline_status`  — po statusie terminu (overdue / on_time / none)
 *   - `company`          — po firmie rekordu, którego dotyczy sprawa (nie po firmie sprawy)
 *   - `status`           — po statusie instancji (active/completed/…); ekrany spraw
 *   - `current_node`     — po etykiecie aktualnego kroku; TYLKO „Moje czekające" (krok pojedynczy)
 */
export const DECISION_GROUP_BY = [
  'process',
  'target_class',
  'submitter',
  'priority',
  'deadline_status',
  'company',
  'status',
  'current_node',
] as const;

export type DecisionGroupBy = (typeof DECISION_GROUP_BY)[number];

/**
 * Białe listy wymiarów grupowania PER EKRAN (podzbiory `DECISION_GROUP_BY`). Każda trasa waliduje
 * `?groupBy=` po swojej liście, żeby ekran nie oferował wymiaru bez sensu dla swojego ziarna.
 *
 *  - „Moje czekające" (krok): 6 wymiarów zachowanych 1:1 (regresja) + `current_node` (krok pojedynczy).
 *  - Ekrany spraw (instancja): `submitter` na „Moje zgłoszenia" pomijamy (zawsze zalogowany user);
 *    `company` na „Aktywne" pomijamy (ekran jest już zawężony do jednej firmy); `current_node`
 *    pomijamy wszędzie na instancji (krok wielowartościowy → tylko filtr, bez grupy).
 */
export const PENDING_DECISION_GROUP_BY = [
  'process',
  'submitter',
  'company',
  'priority',
  'deadline_status',
  'target_class',
  'current_node',
] as const satisfies readonly DecisionGroupBy[];

export const SUBMITTED_INSTANCE_GROUP_BY = [
  'process',
  'target_class',
  'status',
] as const satisfies readonly DecisionGroupBy[];

export const ACTIVE_INSTANCE_GROUP_BY = [
  'process',
  'submitter',
  'target_class',
  'status',
] as const satisfies readonly DecisionGroupBy[];

/**
 * Klucze wiersza sekcji listy decyzji, które należą do systemu, nie do rekordu: `id` (identyfikator
 * rekordu) i `decisions` (kroki decyzji, które trasa sekcji dokłada do każdego wiersza). Pole
 * kartoteki własnej o takim slugu nadpisałoby klucz wiersza (albo go zasłoniło), więc sekcja takich
 * pól NIE czyta: API (`SECTION_RESERVED_FIELD_SLUGS`, `custom-section.ts`) pomija je w katalogu pól,
 * projekcji, sorcie, filtrach i sumach, a panel (`SECTION_ROW_RESERVED_KEY_SET`,
 * `custom-section-kit.tsx`) — w kolumnach. Jedno źródło dla obu stron, żeby się nie rozjechały.
 */
export const SECTION_ROW_RESERVED_KEYS = ['id', 'decisions'] as const satisfies readonly string[];

/**
 * Whitelist modułów dla `ApproverType='record_owner'`.
 *
 * Single source of truth — sprawdzana przez `isRecordOwnerSupportedModule` (niżej) w:
 *   - frontend `apps/admin/src/components/process-builder/ApproverPicker.tsx`
 *     (wyłącza radio option gdy `targetModuleCode` nie należy do listy)
 *   - backend `apps/api/lib/processes/resolver.ts` (`resolveByRecordOwner`)
 *   - backend `apps/api/lib/processes/engine.ts` (`isRecordOwnerMissing` — brak opiekuna)
 *
 * Zakres: `crm_core.contractors` + `crm_core.documents` (T20D.1 — wspólny cel
 * dla wszystkich klas dokumentów). Rozszerzenie listy = zmiana tutaj oraz trzy gałęzie
 * per kartoteka w backendzie:
 *   1. gałąź dispatch w `resolveByRecordOwner` (`resolver.ts`) — kto jest opiekunem;
 *   2. gałąź w `isRecordOwnerMissing` (`engine.ts`) — które pola wiersza rekordu pobranego
 *      w transakcji silnika oznaczają brak opiekuna;
 *   3. gałąź kartoteki w `resolveRecordOwnerMissingField` (`required-fields.ts`) — z którego
 *      katalogu pól wziąć etykietę pola opiekuna do komunikatu.
 *
 * **Rekord bez opiekuna:** resolver czyta pole opiekuna rekordu (`owner_user_id`;
 * u kontrahenta także „Przypisany"). Gdy jest puste, silnik traktuje to jak brak pola
 * wymaganego „Opiekun" — odmowa `REQUIRED_FIELDS_MISSING` z `missing: [{ slug:
 * 'owner_user_id', label }]` (start / decyzja niczego nie zapisują), a nie wada
 * ustawień etapu (`NO_APPROVERS`). Nowy cel na tej liście musi mieć pole
 * `owner_user_id` w katalogu pól kartoteki (etykieta komunikatu).
 */
export const RECORD_OWNER_SUPPORTED_MODULES = [
  'crm_core.contractors',
  'crm_core.documents',
] as const;

/**
 * Czy cel procesu obsługuje akceptanta „opiekun rekordu" (`RECORD_OWNER_SUPPORTED_MODULES`).
 * Jedno sprawdzenie listy dla panelu (kreator etapu) i serwera (resolver, silnik).
 */
export function isRecordOwnerSupportedModule(code: string): boolean {
  return (RECORD_OWNER_SUPPORTED_MODULES as readonly string[]).includes(code);
}

/**
 * Whitelist modułów docelowych dla `processes.targetModuleCode` (F17 MVP).
 *
 * Single source of truth — wartości muszą match'ować klucze backend mapy
 * `TARGET_MODULE_FETCH_MAP` w `apps/api/lib/processes/engine.ts`. Engine używa
 * tych kluczy do pobierania rekordu docelowego przy `POST /:id/start`.
 *
 * **UWAGA:** `system_modules.slug` w DB to BARE slug (np. `'contractors'`),
 * NIE qualified code. Tu trzymamy qualified codes — UI MUSI mapować
 * `slug → qualifiedCode` jeśli pobiera z `/api/system-modules`. W MVP modal
 * "Nowy proces" używa tej listy bezpośrednio (skip fetch).
 *
 * Zakres: `crm_core.contractors` + `crm_core.documents` (T20D.1 — jeden wspólny
 * cel dla wszystkich klas dokumentów; rekord w tabeli `documents`, id globalnie
 * unikalny) + `handel_magazyn.documents`. Rozszerzenie listy = dodać tu + nową gałąź
 * w `TARGET_MODULE_FETCH_MAP` engine'u.
 *
 * `handel_magazyn.documents` jest inny od pozostałych i warto o tym wiedzieć, zanim się go
 * dotknie: przedmiotem obiegu NIE jest dokument w naszej bazie, tylko dokument żyjący
 * w Symfonii Handel. Celem jest wiersz `hm_document_links` — rezerwacja identyfikatora,
 * która wskazuje dokument w KONKRETNEJ bazie Symfonii i niesie zamrożony komplet danych
 * potrzebnych obiegowi. Jeden kod dla trzech kartotek (sprzedaż / magazyn / zamówienia),
 * dokładnie jak `crm_core.documents` obsługuje siedem klas — rozróżnia je pole `kind`.
 */
export const SUPPORTED_PROCESS_TARGET_MODULES = [
  'crm_core.contractors',
  'crm_core.documents',
  'handel_magazyn.documents',
] as const;
export type SupportedProcessTargetModule = (typeof SUPPORTED_PROCESS_TARGET_MODULES)[number];

/**
 * Kod celu dla dokumentów z Symfonii Handel — literał używany w kilku miejscach po obu
 * stronach (silnik, kokpit decyzji, nawigacja frontu), więc ma jedno źródło.
 */
export const HANDEL_TARGET_MODULE_CODE = 'handel_magazyn.documents';
